import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Eye } from 'lucide-react';

import { NamedLink } from '../../../../components';
import MarketplaceCard from '../../../../components/MarketplaceCard/MarketplaceCard';

import css from './ResalePlatformSection.module.css';
import buttonCss from '../../../LandingPage/components/HomepageButtons.module.css';

const RESALE_MIN_STOCK = 1;
const RESALE_STOCK_MODE = 'match-undefined';

const trackReviewCommerceEvent = (eventName, payload = {}) => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, payload);
  }
};

const ResalePlatformSectionComponent = ({ listings = [], onFetchListings, reviewData }) => {
  const publicData = reviewData?.attributes?.publicData || {};
  const reviewId = reviewData?.id?.uuid;
  const brandSlug = publicData.brandSlug;
  const color = publicData.color;

  useEffect(() => {
    onFetchListings({ brandSlug, color });
  }, [onFetchListings, brandSlug, color]);

  const displayListings = listings.slice(0, 5);

  return (
    <section className={css.resalePlatformSection}>
      <div className={css.container}>
        <div className={css.stackLayout}>
          <div className={css.promoHeader}>
            <div className={css.promoHeaderCopy}>
              <h2 className={css.promoTitle}>Resale</h2>
              <p className={css.promoText}>Buy the bag. Sell the dress. Keep 80%. Repeat.</p>
            </div>
            <NamedLink
              name="ResalePage"
              className={`${css.viewAllButton} ${buttonCss.button} ${buttonCss.buttonSecondary} ${buttonCss.buttonLight}`}
            >
              <Eye aria-hidden="true" className={buttonCss.icon} strokeWidth={2} fill="none" />
              <span>VIEW ALL</span>
            </NamedLink>
          </div>

          <div className={css.productGrid}>
            {displayListings.map(item => {
              return (
                <div
                  key={item.id.uuid}
                  onClickCapture={() =>
                    trackReviewCommerceEvent('review_to_product_click', {
                      source_position: 'resale_section_card',
                      review_id: reviewId,
                      product_id: item.id.uuid,
                      brand: brandSlug || null,
                    })
                  }
                >
                  <MarketplaceCard listing={item} className={css.marketplaceCard} />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

const getStockQuantity = (listing, stockEntities) => {
  const stockId = listing?.relationships?.currentStock?.data?.id?.uuid;
  if (!stockId) {
    return null;
  }

  const quantity = stockEntities?.[stockId]?.attributes?.quantity;
  return typeof quantity === 'number' ? quantity : null;
};

const mapStateToProps = (state, ownProps) => {
  const reviewPublicData = ownProps.reviewData?.attributes?.publicData || {};
  const reviewBrandSlug = reviewPublicData.brandSlug;
  const reviewColor = reviewPublicData.color;

  // Get all resale listings directly from marketplaceData entities
  const entities = state.marketplaceData?.entities || {};
  const listingEntities = entities.listing || {};
  const stockEntities = entities.stock || {};

  const publishedResaleListings = Object.values(listingEntities).filter(listing => {
    const listingType = listing.attributes?.publicData?.listingType;
    const isSyncHidden = listing.attributes?.publicData?._devSyncHidden === true;
    const stockQuantity = getStockQuantity(listing, stockEntities);
    return (
      listingType === 'sell-used-products' &&
      listing.attributes?.state === 'published' &&
      !isSyncHidden &&
      (stockQuantity === null || stockQuantity >= RESALE_MIN_STOCK)
    );
  });

  const matchedListings = publishedResaleListings.filter(listing => {
    const listingPublicData = listing.attributes?.publicData || {};
    const brandMatch = reviewBrandSlug ? listingPublicData.brandSlug === reviewBrandSlug : true;
    const colorMatch =
      reviewColor && reviewColor !== 'unknown' ? listingPublicData.color === reviewColor : true;
    return brandMatch && colorMatch;
  });

  const sourceListings = matchedListings.length > 0 ? matchedListings : publishedResaleListings;

  const listings = sourceListings.slice(0, 12).map(listing => {
    // Denormalize images
    const imageRefs = listing.relationships?.images?.data || [];
    const images = imageRefs.map(ref => entities.image?.[ref.id.uuid]).filter(Boolean);

    return {
      ...listing,
      images,
    };
  });

  return { listings };
};

const mapDispatchToProps = dispatch => ({
  onFetchListings: ({ brandSlug, color } = {}) => {
    return dispatch((dispatch, getState, sdk) => {
      const baseQuery = {
        pub_listingType: 'sell-used-products',
        minStock: RESALE_MIN_STOCK,
        stockMode: RESALE_STOCK_MODE,
        perPage: 12,
        include: ['images', 'currentStock'],
        'fields.image': [
          'variants.scaled-medium',
          'variants.scaled-small',
          'variants.listing-card',
        ],
      };

      const filteredQuery = {
        ...baseQuery,
        ...(brandSlug ? { pub_brandSlug: brandSlug } : {}),
        ...(color && color !== 'unknown' ? { pub_color: color } : {}),
      };

      const { addMarketplaceEntities } = require('../../../../ducks/marketplaceData.duck');

      return sdk.listings
        .query(filteredQuery)
        .then(response => {
          dispatch(addMarketplaceEntities(response));

          const hasResults = (response?.data?.data || []).length > 0;
          if (hasResults || (!brandSlug && !color)) {
            return response;
          }

          return sdk.listings.query(baseQuery).then(fallbackResponse => {
            dispatch(addMarketplaceEntities(fallbackResponse));
            return fallbackResponse;
          });
        })
        .catch(() =>
          sdk.listings.query(baseQuery).then(fallbackResponse => {
            dispatch(addMarketplaceEntities(fallbackResponse));
            return fallbackResponse;
          })
        );
    });
  },
});

const ResalePlatformSection = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ResalePlatformSectionComponent);

export default ResalePlatformSection;
